/**
 * Now — the answer to "what's happening with Arsenal right now?"
 *
 * One hero surface driven by the match-live feed's phase machine:
 *   pre   → scoreboard countdown, crowd prediction, calendar reminder
 *   live  → scoreline, minute, events timeline (goal bursts + floodlights)
 *   ht    → same, paused
 *   ft    → result + the Gauge
 *   idle  → next match countdown, last result + fan mood, news, this-day
 *
 * Fact meets fiction: every factual block carries the fans' temperature
 * beside it — crowd confidence under the fixture, the terrace mood under
 * the last result, the gauge over the final score.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EmptyNote, PHASE_LABEL, Rule, ScoreLine, StaleBadge, useOffline } from '../components/chrome';
import { Floodlight, fireGoalBurst, haptic } from '../components/Floodlight';
import { GaugePanel } from '../components/Gauge';
import { PredictionStrip } from '../components/PredictionStrip';
import { CommunityScore, currentCommunityReport, FreeDataAttribution } from '../components/FreeData';
import { WatchChip } from '../components/WatchChip';
import { fetchTakes } from '../lib/api';
import { useLivingFeed } from '../lib/living';
import { LivingDesk } from '../components/LivingDesk';
import { mayPoll } from '../lib/public-reads';
import { FEEDS, asOfLabel, headlineKey, isCurrentLondonEdition, useFeed } from '../lib/feeds';
import { addToCalendar } from '../lib/ics';
import { broadcastMatchStarting, broadcastScoreUpdated } from '../lib/intents';
import { useModalFocus } from '../lib/useModalFocus';
import { aggregateH2H } from '../lib/lore';
import { countdownTo, fixtureTimeLabel, thisDayKey } from '../lib/matchday';
import { arsenalIsAway, arsenalScore } from '../lib/score';
import { championsMemoryFromClub } from '../lib/verifiedChampions';
import type {
  ClubFeed,
  Fixture,
  H2HInsight,
  LoreEntry,
  MatchEvent,
  MatchFeed,
  FixturesFeed,
  Player,
  TodayBriefItem,
  TodayCertainty,
  TodayDispatch,
  Take,
} from '../lib/types';

const pad = (n: number) => String(n).padStart(2, '0');

const fourHours = 4 * 60 * 60 * 1000;

function fixtureKickoffMs(fixture: Pick<Fixture, 'kickoffUtc' | 'kickoffDate'>): number {
  if (fixture.kickoffUtc) return Date.parse(fixture.kickoffUtc);
  return fixture.kickoffDate ? Date.parse(`${fixture.kickoffDate}T23:59:59Z`) : Number.NaN;
}

/**
 * Select a safe homepage fixture. A scheduled row cannot stay under NEXT UP
 * after its kick-off has passed. During the four-hour reconciliation window it
 * is shown as awaiting verification; after that, a verified future fixture is
 * selected if one exists. No score or final state is inferred from the clock.
 */
export function selectNowMatch(match: MatchFeed, fixtures: FixturesFeed, nowMs = Date.now()): {
  match: MatchFeed;
  awaitingResultFor: string | null;
  lastCheckedAt: string | null;
} {
  const current = fixtures.fixtures.find((fixture) => fixture.id === match.matchId);
  const currentKickoff = fixtureKickoffMs(current ?? match);
  const currentPending = Boolean(
    ['idle', 'pre'].includes(match.phase) &&
    match.score == null &&
    Number.isFinite(currentKickoff) && currentKickoff <= nowMs,
  );
  const future = fixtures.fixtures
    .filter((fixture) => fixture.status === 'scheduled' && fixture.statusQuality !== 'overdue' && fixture.id !== match.matchId)
    .filter((fixture) => Number.isFinite(fixtureKickoffMs(fixture)) && fixtureKickoffMs(fixture) > nowMs)
    .sort((a, b) => fixtureKickoffMs(a) - fixtureKickoffMs(b))[0];
  const checked = match.matchId === current?.id
    ? fixtures.editorial?.checkedAt ?? fixtures.source?.checkedAt ?? null
    : null;
  if (currentPending && future && currentKickoff <= nowMs - fourHours) {
    return {
      match: {
        ...match,
        matchId: future.id,
        kickoffUtc: future.kickoffUtc,
        kickoffDate: future.kickoffDate,
        comp: future.comp,
        opponent: future.opponent,
        opponentShort: future.opponentShort,
        venue: future.venue,
        arsenalSide: future.arsenalSide,
        ground: future.ground,
        timeTbc: future.timeTbc,
        tv: future.tv,
        matchStatus: future.status === 'postponed' ? 'postponed' : 'scheduled',
        phase: 'idle',
        score: null,
        minute: null,
        events: [],
        communityReport: null,
      },
      awaitingResultFor: match.matchId,
      lastCheckedAt: checked,
    };
  }
  return { match, awaitingResultFor: currentPending ? match.matchId : null, lastCheckedAt: checked };
}

// ── Collective H2H read (pre/idle only) ──────────────────────────────────────
/**
 * One calm sentence summarising what the loaded lore says about an opponent.
 * Uses the same data the H2HFeel panel in MatchDetail unfolds — just the
 * headline, not the full card. Gate: insight.n === 0 → return null (nothing).
 */
function h2hHeadline(insight: H2HInsight): string | null {
  if (insight.n === 0) return null;
  const { n, record, calibration, mood } = insight;
  const gamesWord = n === 1 ? 'game' : 'games';
  // Primary clause: the W/D/L record across n meetings.
  let sentence = `Across ${n} ${gamesWord} in the book: ${record.w}W ${record.d}D ${record.l}L`;
  // Append the dominant home or away mood when present.
  const homeMood = mood.home.mood;
  const awayMood = mood.away.mood;
  if (homeMood && awayMood && homeMood !== awayMood) {
    sentence += ` — at home the North Bank runs ${homeMood}, away ${awayMood}`;
  } else if (homeMood || awayMood) {
    sentence += ` — the North Bank tends to run ${homeMood ?? awayMood}`;
  }
  // Append calibration read if we have enough calls (caveat 'ok').
  if (calibration.caveat === 'ok' && calibration.value !== null) {
    sentence += `. Called right ${calibration.value}% of the time (${calibration.n} game${calibration.n === 1 ? '' : 's'})`;
  } else if (calibration.caveat === 'raw' && calibration.fraction) {
    sentence += `. Called right ${calibration.fraction} — too early to call a trend`;
  }
  // Ensure it ends with a period.
  if (!sentence.endsWith('.')) sentence += '.';
  return sentence;
}

const H2HReadBlock = ({ entries, opponent }: { entries: LoreEntry[]; opponent: string }) => {
  const insight = aggregateH2H(entries, opponent);
  const headline = h2hHeadline(insight);
  if (!headline) return null;
  return (
    <>
      <Rule label="The read" accent="var(--gold)" />
      <blockquote className="oracle h2h-now-read">
        <p>{headline}</p>
      </blockquote>
    </>
  );
};

// ── Countdown ────────────────────────────────────────────────────────────────
const CountdownBlock = ({ kickoffUtc }: { kickoffUtc: string }) => {
  const [now, setNow] = useState(() => Date.now());
  const cd = countdownTo(kickoffUtc, now);

  useEffect(() => {
    // Tick every second inside 24h (the scoreboard moment), per minute beyond.
    const fast = cd.totalMs < 86_400_000;
    const id = setInterval(() => setNow(Date.now()), fast ? 1000 : 60_000);
    return () => clearInterval(id);
  }, [cd.totalMs < 86_400_000]);

  if (cd.totalMs <= 0) {
    return <div className="countdown countdown--zero">Kick-off</div>;
  }
  if (cd.days > 0) {
    return (
      <div className="countdown">
        <span className="countdown-cell">
          <strong>{cd.days}</strong>
          <em>day{cd.days === 1 ? '' : 's'}</em>
        </span>
        <span className="countdown-cell">
          <strong>{pad(cd.hours)}</strong>
          <em>hrs</em>
        </span>
        <span className="countdown-cell">
          <strong>{pad(cd.minutes)}</strong>
          <em>min</em>
        </span>
      </div>
    );
  }
  return (
    <div className="countdown countdown--close">
      <span className="countdown-cell">
        <strong>{pad(cd.hours)}</strong>
        <em>hrs</em>
      </span>
      <span className="countdown-cell">
        <strong>{pad(cd.minutes)}</strong>
        <em>min</em>
      </span>
      <span className="countdown-cell">
        <strong key={cd.seconds} className="tick">
          {pad(cd.seconds)}
        </strong>
        <em>sec</em>
      </span>
    </div>
  );
};

// ── Events timeline ──────────────────────────────────────────────────────────
const EVENT_GLYPH: Record<MatchEvent['type'], string> = {
  goal: '●',
  'own-goal': '◐',
  pen: '◉',
  red: '▮',
  yellow: '▯',
  sub: '⇄',
  var: 'VAR',
  note: '·',
};

/** Match an event's player string to a squad member for cross-linking. */
function squadMatch(players: Player[], name?: string): Player | null {
  if (!name) return null;
  const lower = name.toLowerCase();
  return (
    players.find((p) => lower.includes(p.full.toLowerCase()) || lower.includes(p.name.toLowerCase())) ??
    null
  );
}

const EventsTimeline = ({
  events,
  players,
  onOpenPlayer,
}: {
  events: MatchEvent[];
  players: Player[];
  onOpenPlayer: (id: string) => void;
}) => {
  if (events.length === 0) return <EmptyNote>No events yet. Hold tight.</EmptyNote>;
  return (
    <ol className="events">
      {[...events]
        .sort((a, b) => b.min - a.min)
        .map((e, i) => {
          const linked = e.ours ? squadMatch(players, e.player) : null;
          return (
            <li key={i} className={`event${e.ours ? ' event--ours' : ''}`}>
              <span className="event-min">{e.min}&prime;</span>
              <span className={`event-glyph event-glyph--${e.type}`}>{EVENT_GLYPH[e.type]}</span>
              <span className="event-body">
                {linked ? (
                  <button className="player-link" onClick={() => onOpenPlayer(linked.id)}>
                    {e.player}
                  </button>
                ) : (
                  (e.player ?? e.detail ?? e.type)
                )}
                {e.player && e.detail ? <em> — {e.detail}</em> : null}
              </span>
            </li>
          );
        })}
    </ol>
  );
};

export const NorthBankLive = ({ matchId, onOpen }: { matchId: string; onOpen: () => void }) => {
  const [takes, setTakes] = useState<Take[]>([]);
  const [online, setOnline] = useState(true);
  const loadGeneration = useRef(0);

  useEffect(() => {
    let active = true;
    let timer: number | null = null;
    let lastCheck = -Infinity;
    const schedule = () => {
      if (!active || !mayPoll()) return;
      timer = window.setTimeout(load, 60_000);
    };
    const load = () => {
      if (!active || !mayPoll()) return;
      if (timer !== null) window.clearTimeout(timer);
      if (Date.now() - lastCheck < 60_000) { schedule(); return; }
      lastCheck = Date.now();
      const generation = ++loadGeneration.current;
      fetchTakes(matchId).then((result) => {
        if (!active || generation !== loadGeneration.current) return;
        setTakes(result.takes.filter((take) => take.thread === 'MATCH').slice(0, 4));
        setOnline(result.online);
      }).finally(schedule);
    };
    load();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') load();
      else if (timer !== null) window.clearTimeout(timer);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', load);
    return () => {
      active = false;
      if (timer !== null) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', load);
    };
  }, [matchId]);

  return (
    <section className="north-bank-live" aria-label="North Bank live supporter commentary">
      <div className="north-bank-live-head">
        <div>
          <span className="north-bank-live-kicker">Original supporter commentary</span>
          <h2>North Bank live</h2>
        </div>
        <span className="north-bank-live-status" role="status">
          <i aria-hidden="true" /> {online ? 'Refreshes each minute while visible' : 'Offline snapshot'}
        </span>
      </div>
      <div aria-live="polite" aria-atomic="false">
        {takes.length > 0 ? (
          <ol className="north-bank-live-list">
            {takes.map((take) => (
              <li key={take.id}>
                <p>{take.text}</p>
                <footer>
                  <strong>{take.handle}</strong>
                  <span>▲ {take.up}</span>
                </footer>
              </li>
            ))}
          </ol>
        ) : (
          <p className="north-bank-live-empty">The live wire is quiet. Be the first voice in.</p>
        )}
      </div>
      <button className="ghost-btn" onClick={onOpen}>
        Join the match thread
      </button>
    </section>
  );
};

const ChampionsPinnedCard = ({
  lastSeason,
  onOpenEntry,
}: {
  lastSeason: ClubFeed['lastSeason'];
  onOpenEntry?: (id: string) => void;
}) => {
  const memory = championsMemoryFromClub(lastSeason);
  if (!memory) return null;
  return (
    <>
      <Rule label="Pinned" accent="var(--gold)" />
      <section className="champions-card champions-card--pinned" aria-label="Champions summary">
        <span className="champions-label">{memory.label}</span>
        <h2 className="champions-headline">{memory.title}</h2>
        <p className="champions-fan-note">{memory.note}</p>
        <div className="champions-stats champions-stats--pinned">
          {memory.stats.map((s) => (
            <span key={s.l}>
              <strong>{s.v}</strong>
              <em>{s.l}</em>
            </span>
          ))}
        </div>
        <p className="champions-scorers champions-scorers--pinned">
          League goals: {memory.scorers.map((p) => `${p.n} ${p.v}`).join(' · ')}
        </p>
        <p className="champions-fan-detail">{memory.detail}</p>
        {onOpenEntry && (
          <button className="champions-more" onClick={() => onOpenEntry(memory.entryId)}>
            Relive the title season →
          </button>
        )}
      </section>
    </>
  );
};

// ── Today Lead Dispatch — the Arsenal morning paper under the red header ──────
/**
 * A calm, compact briefing: one lead read and up to three top updates. The
 * full edition remains available behind one disclosure; deeper discussion
 * lives in the North Bank tab and in report detail sheets.
 */
const CERTAINTY_LABEL: Record<TodayCertainty, string> = {
  official: 'Official',
  verified: 'Verified',
  reported: 'Reported',
  developing: 'Developing',
  unconfirmed: 'Unconfirmed',
};

const LANE_LABEL: Record<string, string> = {
  players: 'Players',
  transfers: 'Transfers',
  club: 'Club',
  lead: 'Lead report',
  match: 'Match',
  international: 'Gunners abroad',
};

/** Provenance line, honesty-gated: "Verified · 3 sources" only when ≥2 back it. */
function provenanceLine(item: TodayBriefItem): string {
  const label = CERTAINTY_LABEL[item.certainty];
  if (item.certainty === 'verified' && typeof item.sources === 'number' && item.sources >= 2) {
    return `${label} · ${item.sources} sources`;
  }
  return `${label} · ${item.source}`;
}

/**
 * The press-mark grammar — one glyph vocabulary, used everywhere a fact
 * renders (lead, lane, change-row, worldwatch card, overlay): "official" is
 * the only tier that earns a solid ◆; "verified" only earns that same solid
 * ◆ once an external source count actually backs it (the same ≥2 gate
 * `provenanceLine` uses for "N sources" — a "verified" item with no counted
 * source is not allowed to look identical to one with two); "reported" gets
 * a hollow ◇ — something, but not yet corroborated; "developing" and
 * "unconfirmed" carry no bare glyph at all. For those two the spelled-out
 * word in `provenanceLine` IS the entire signal, so no mark ever overclaims
 * what's actually known. Desk/archive notes never call this — they render
 * `DESK_NOTE_LABEL` instead (see `isDeskNote`).
 */
function provenanceMark(item: TodayBriefItem): '◆' | '◇' | null {
  if (item.certainty === 'official') return '◆';
  if (item.certainty === 'verified' && typeof item.sources === 'number' && item.sources >= 2) return '◆';
  if (item.certainty === 'reported') return '◇';
  return null;
}

/** The press-mark glyph, rendered only when `provenanceMark` earns one. */
const ProvenanceMark = ({ item }: { item: TodayBriefItem }) => {
  const glyph = provenanceMark(item);
  if (!glyph) return null;
  return (
    <span className="today-prov-mark" aria-hidden="true">
      {glyph}
    </span>
  );
};

/**
 * "COYG desk"/"COYG archive" is COYG talking about itself — an
 * editorial or engineering note, not an external report. Those items get a
 * distinct "From the desk" treatment (no certainty badge, italic editorial
 * type) instead of the verified/official/reported machinery, which is
 * reserved for claims an outside source actually backs. Mirrors the guard in
 * `lib/season-checks.ts` (`desk-archive-certainty`) — the build fails long
 * before this render path could show a fabricated certainty mark.
 */
const DESK_SOURCE_RE = /COYG (desk|archive)/i;
function isDeskNote(item: TodayBriefItem): boolean {
  return DESK_SOURCE_RE.test(item.source);
}
const DESK_NOTE_LABEL = 'From the desk';

/** A change-wire row's time, in Europe/London (the club's clock). */
function changeTime(iso: string, precision: 'date' | 'datetime' = 'datetime'): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  try {
    if (precision === 'date') return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/London' });
    return new Date(t).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/London',
    });
  } catch {
    return '';
  }
}

const TodayBriefOverlay = ({
  item,
  onClose,
  onOpenMatch,
  onOpenPlayer,
  onOpenEntry,
}: {
  item: TodayBriefItem;
  onClose: () => void;
  onOpenMatch: (matchId: string) => void;
  onOpenPlayer: (playerId: string) => void;
  onOpenEntry?: (entryId: string) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const body = item.body ?? item.summary;
  const sourceLinks = [...new Set(item.sourceUrls?.length ? item.sourceUrls : [item.sourceUrl])];
  useModalFocus(ref, onClose);

  const sheet = (
    <div className="today-overlay-backdrop" onClick={onClose}>
      <div
        ref={ref}
        className="today-overlay"
        role="dialog"
        aria-modal="true"
        aria-label={item.title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="today-overlay-top">
          <span className="today-overlay-kicker">{LANE_LABEL[item.category] ?? item.category}</span>
          <button className="today-overlay-close" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        <h2 className={`today-overlay-title${isDeskNote(item) ? ' today-overlay-title--desk' : ''}`}>{item.title}</h2>
        <div className="today-overlay-body">
          {body.split(/\n{2,}/).map((paragraph, i) => (
            <p key={i}>{paragraph}</p>
          ))}
        </div>
        {isDeskNote(item) ? (
          <p className="today-desk-mark">{DESK_NOTE_LABEL}</p>
        ) : (
          <div className="today-overlay-source">
            <p className={`today-prov today-cert--${item.certainty}`}>
              <ProvenanceMark item={item} />
              {provenanceLine(item)}
            </p>
            <div className="today-source-links" aria-label="Story sources">
              {sourceLinks.map((url, index) => (
                <a key={url} className="today-source-link" href={url} target="_blank" rel="noreferrer">
                  {index === 0 ? 'Open primary source' : `Open source ${index + 1}`} ↗
                </a>
              ))}
            </div>
          </div>
        )}
        {item.related && item.related.length > 0 && (
          <div className="today-related">
            {item.related.map((r) => {
              if (r.kind === 'match') {
                return (
                  <button key={r.id} className="today-related-link" onClick={() => onOpenMatch(r.id)}>
                    {r.label}
                  </button>
                );
              }
              if (r.kind === 'player') {
                return (
                  <button key={r.id} className="today-related-link" onClick={() => onOpenPlayer(r.id)}>
                    {r.label}
                  </button>
                );
              }
              if (r.kind === 'entry' && onOpenEntry) {
                return (
                  <button key={r.id} className="today-related-link" onClick={() => onOpenEntry(r.id)}>
                    {r.label}
                  </button>
                );
              }
              return (
                <span key={r.id} className="today-related-tag">
                  {r.label}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
  return createPortal(sheet, document.body);
};

// Collapse by id AND by normalised headline so the read report never shows the
// same story twice — even when the lead is echoed in a lane or two dispatch
// items carry the same headline under different ids (the "duplicate headlines"
// the report previously showed, since the old dedup was id-only).
export function uniqueTodayItems(items: TodayBriefItem[]): TodayBriefItem[] {
  const seenIds = new Set<string>();
  const seenHeadlines = new Set<string>();
  const out: TodayBriefItem[] = [];
  for (const item of items) {
    const hk = headlineKey(item.title, item.source);
    if (seenIds.has(item.id) || seenHeadlines.has(hk)) continue;
    seenIds.add(item.id);
    seenHeadlines.add(hk);
    out.push(item);
  }
  return out;
}

const TODAY_GROUP_ORDER: TodayBriefItem['category'][] = [
  'lead',
  'match',
  'players',
  'international',
  'transfers',
  'club',
];

const TodayReportOverlay = ({
  items,
  onClose,
  onOpenItem,
}: {
  items: TodayBriefItem[];
  onClose: () => void;
  onOpenItem: (item: TodayBriefItem) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useModalFocus(ref, onClose);

  const groups = TODAY_GROUP_ORDER
    .map((category) => ({ category, items: items.filter((item) => item.category === category) }))
    .filter((group) => group.items.length > 0);

  const sheet = (
    <div className="today-overlay-backdrop" onClick={onClose}>
      <div
        ref={ref}
        className="today-overlay today-report"
        role="dialog"
        aria-modal="true"
        aria-label="Today's COYG report"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="today-overlay-top">
          <span className="today-overlay-kicker">Today's report</span>
          <button className="today-overlay-close" onClick={onClose} aria-label="Close">
            Close
          </button>
        </div>
        <h2 className="today-overlay-title">Everything worth knowing today</h2>
        <div className="today-report-proof">
          <strong>COYG verified pipeline</strong>
          <span>Each item carries its source and certainty — nothing is published without both.</span>
        </div>
        <div className="today-report-groups">
          {groups.map((group) => (
            <section key={group.category} className="today-report-group">
              <h3>{LANE_LABEL[group.category] ?? group.category}</h3>
              <div className="today-report-list">
                {group.items.map((item) => {
                  const detail = (item.body ?? item.summary).replace(/\s+/g, ' ').trim();
                  const desk = isDeskNote(item);
                  return (
                    <button key={item.id} className="today-report-item" onClick={() => onOpenItem(item)}>
                      <span className={`today-report-item-title${desk ? ' today-report-item-title--desk' : ''}`}>
                        {item.title}
                      </span>
                      <span className="today-report-item-summary">{item.summary}</span>
                      {detail && detail !== item.summary && (
                        <span className="today-report-item-detail">{detail}</span>
                      )}
                      {desk ? (
                        <span className="today-desk-mark today-desk-mark--mini">{DESK_NOTE_LABEL}</span>
                      ) : (
                        <span className={`today-prov today-prov--mini today-cert--${item.certainty}`}>
                          <ProvenanceMark item={item} />
                          {provenanceLine(item)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
  return createPortal(sheet, document.body);
};

const TodayDispatchBlock = ({
  today,
  stale,
  asOf,
  onOpenMatch,
  onOpenPlayer,
  onOpenEntry,
}: {
  today: TodayDispatch;
  stale: boolean;
  asOf: string | null;
  onOpenMatch: (matchId: string) => void;
  onOpenPlayer: (playerId: string) => void;
  onOpenEntry?: (entryId: string) => void;
}) => {
  const [open, setOpen] = useState<TodayBriefItem | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const offline = useOffline();
  const allItems = uniqueTodayItems([today.lead, ...today.lanes, ...(today.changes ?? []), ...(today.more ?? [])]);
  const updates = uniqueTodayItems([...today.lanes, ...(today.changes ?? []), ...(today.more ?? [])]).slice(0, 3);
  const visibleIds = new Set([today.lead.id, ...updates.map((item) => item.id)]);
  const overflowCount = allItems.filter((item) => !visibleIds.has(item.id)).length;
  const reportCount = allItems.length;

  const londonDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
  if (today.date !== londonDate) {
    const edition = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      day: 'numeric',
      month: 'long',
    }).format(new Date(`${today.date}T12:00:00Z`));
    return (
      <>
        <Rule label="Briefing" />
        <div className="today-awaiting" role="status">
          <strong>No fresh desk edition yet</strong>
          <span>The last COYG briefing was {edition}. We will never pass an old edition off as today.</span>
        </div>
      </>
    );
  }

  return (
    <>
      <Rule
        label="Today in 90 seconds"
        right={
          <details className={`today-edition-proof${stale || offline ? ' today-edition-proof--warning' : ''}`}>
            <summary>{offline ? 'Offline · saved copy' : stale ? 'Edition check needed' : 'Edition verified'}</summary>
            <div>
              <strong className="today-updated">{offline ? 'Saved on this device' : today.updatedAt ? `Updated ${changeTime(today.updatedAt)}` : 'Current desk edition'}</strong>
              <span>{offline ? 'Reconnect for the latest confirmed edition. ' : asOf ? `Published ${asOfLabel(asOf)}. ` : ''}Every report opens its named source and certainty.</span>
            </div>
          </details>
        }
      />
      <p className="today-edition-deck">One lead. What moved. What matters.</p>

      <button className="today-lead" onClick={() => setOpen(today.lead)}>
        <span className="today-lead-label">Lead report</span>
        <span className="today-lead-title">{today.lead.title}</span>
        <span className="today-lead-summary">{today.lead.summary}</span>
        <span className={`today-prov today-cert--${today.lead.certainty}`}>
          <ProvenanceMark item={today.lead} />
          {provenanceLine(today.lead)}
        </span>
      </button>

      {updates.length > 0 && (
        <div className="today-updates" aria-label="Top updates">
          {updates.map((item) => {
            const desk = isDeskNote(item);
            return (
              <button key={item.id} className={`today-update${desk ? ' today-update--desk' : ''}`} onClick={() => setOpen(item)}>
                <span className="today-update-label">
                  {LANE_LABEL[item.category] ?? item.category}
                </span>
                {item.storyCount && item.storyCount > 1 ? (
                  <span className="today-update-count">{item.storyCount} updates</span>
                ) : null}
                <span className={`today-update-title${desk ? ' today-update-title--desk' : ''}`}>{item.title}</span>
                <span className="today-update-summary">{item.summary}</span>
                {desk ? (
                  <span className="today-desk-mark today-desk-mark--mini">{DESK_NOTE_LABEL}</span>
                ) : (
                  <span className={`today-prov today-prov--mini today-cert--${item.certainty}`}>
                    <ProvenanceMark item={item} />
                    {provenanceLine(item)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {reportCount > 1 && (
        <button className="today-full-report" onClick={() => setReportOpen(true)}>
          <span>Read the full edition</span>
          <em>{overflowCount > 0 ? `${overflowCount} more` : `${reportCount} items`}</em>
        </button>
      )}

      {open && (
        <TodayBriefOverlay
          item={open}
          onClose={() => setOpen(null)}
          onOpenMatch={(id) => {
            setOpen(null);
            onOpenMatch(id);
          }}
          onOpenPlayer={(id) => {
            setOpen(null);
            onOpenPlayer(id);
          }}
          onOpenEntry={(id) => {
            setOpen(null);
            onOpenEntry?.(id);
          }}
        />
      )}

      {reportOpen && (
        <TodayReportOverlay
          items={allItems}
          onClose={() => setReportOpen(false)}
          onOpenItem={(item) => {
            setReportOpen(false);
            setOpen(item);
          }}
        />
      )}
    </>
  );
};

// ── The screen ───────────────────────────────────────────────────────────────
export const NowScreen = ({
  onOpenMatch,
  onOpenTerrace,
  onOpenPlayer,
  onOpenEntry,
}: {
  onOpenMatch: (matchId: string) => void;
  onOpenTerrace: () => void;
  onOpenPlayer: (id: string) => void;
  onOpenEntry?: (id: string) => void;
}) => {
  const isLivePhase = (m: MatchFeed) => m.phase === 'live' || m.phase === 'ht';
  // App and screen share one conservative, kick-off-aware scheduler.
  const match = useFeed(FEEDS.match);
  const fixtures = useFeed(FEEDS.fixtures);
  const news = useFeed(FEEDS.news);
  const living = useLivingFeed();
  const club = useFeed(FEEDS.club);
  const squad = useFeed(FEEDS.squad);
  const codex = useFeed(FEEDS.codex);
  const lore = useFeed(FEEDS.lore);
  const selection = useMemo(() => selectNowMatch(match.data, fixtures.data), [match.data, fixtures.data]);
  const m = selection.match;
  const report = currentCommunityReport(m, match.stale);
  const awaitingResult = Boolean(selection.awaitingResultFor) && !report;
  const awaitingResultOnly = awaitingResult && selection.awaitingResultFor === m.matchId;
  const liveIsCurrent = !match.stale && isLivePhase(m);
  const staleLive = match.stale && isLivePhase(m);
  const presentationPhase = staleLive ? 'idle' : m.phase;
  const arsenalAway = arsenalIsAway(m.venue, m.arsenalSide);
  const liveAnnouncement = useMemo(() => {
    if (!liveIsCurrent) return '';
    const score = m.score
      ? `Arsenal ${arsenalAway ? m.score.away : m.score.home}, ${m.opponent} ${arsenalAway ? m.score.home : m.score.away}`
      : 'Score unavailable';
    const minute = m.phase === 'ht' ? 'Half-time' : m.minute == null ? 'Minute unavailable' : `${m.minute} minutes`;
    const latest = m.events.at(-1);
    const event = latest ? `Latest event: ${latest.min} minutes, ${latest.player ?? latest.detail ?? latest.type}.` : 'No events yet.';
    return `${minute}. ${score}. ${event}`;
  }, [arsenalAway, liveIsCurrent, m]);
  const heroRef = useRef<HTMLElement>(null);

  // Cross-app intents + the goal salvo when OUR score moves.
  const prevScore = useRef<string | null>(null);
  const prevOurs = useRef<number>(-1);
  const announcedLive = useRef<string | null>(null);
  useEffect(() => {
    if (liveIsCurrent && announcedLive.current !== m.matchId && m.kickoffUtc) {
      announcedLive.current = m.matchId;
      broadcastMatchStarting(m.matchId, m.opponent, m.kickoffUtc);
    }
    if (m.score) {
      // Arsenal's goals are score.away at an away fixture — celebrate OURS, not theirs.
      const ours = arsenalScore(m.score, m.venue, m.arsenalSide)!.ours;
      const key = `${m.matchId}:${m.score.home}-${m.score.away}`;
      if (prevScore.current && prevScore.current !== key && liveIsCurrent) {
        broadcastScoreUpdated(m.matchId, m.score.home, m.score.away, m.minute);
      }
      if (prevOurs.current >= 0 && ours > prevOurs.current && liveIsCurrent) {
        fireGoalBurst(heroRef.current);
        haptic(30);
      }
      prevScore.current = key;
      prevOurs.current = ours;
    } else {
      prevOurs.current = -1;
    }
  }, [liveIsCurrent, m.matchId, m.score?.home, m.score?.away, m.phase, m.venue, m.arsenalSide]);

  const thisDay = useMemo(() => club.data.thisDay?.[thisDayKey()] ?? null, [club.data]);

  // "From the archive" — a calm, single-row on-ramp from the idle Now screen.
  // One verified
  // entry, rotated by the day so it feels fresh without being random on every
  // render, deep-linking into the entry via onOpenEntry.
  const vaultPick = useMemo(() => {
    const verified = codex.data.entries.filter((e) => e.verification?.status === 'verified');
    if (verified.length === 0) return null;
    const dayIndex = Math.floor(Date.now() / 86_400_000) % verified.length;
    return verified[dayIndex];
  }, [codex.data]);

  const heroFixture: Fixture = {
    id: m.matchId,
    kickoffUtc: m.kickoffUtc,
    kickoffDate: m.kickoffDate,
    comp: m.comp ?? '',
    opponent: m.opponent,
    opponentShort: m.opponentShort ?? m.opponent.slice(0, 3).toUpperCase(),
    venue: (m.venue as Fixture['venue']) ?? 'H',
    arsenalSide: m.arsenalSide,
    ground: m.ground,
    status: m.matchStatus === 'postponed' ? 'postponed' : 'scheduled',
    timeTbc: m.timeTbc,
  };

  const venueLabel =
    m.venue === 'H' ? 'Home · Emirates Stadium' : m.venue === 'A' ? 'Away' : m.ground ?? 'Neutral venue';

  // Broadcaster: the match-live feed may not carry it, so fall back to the
  // matching fixture. Never guessed — null when neither knows (§watch-chip).
  const resolvedTv = useMemo<string | null>(() => {
    const fx = fixtures.data.fixtures.find((f) => f.id === m.matchId);
    return m.tv ?? fx?.tv ?? null;
  }, [m.tv, m.matchId, fixtures.data.fixtures]);

  // Our full-time result, from the data already present: at an away fixture
  // Arsenal are score.away. Drives the FT scoreline's tint (win → gold-pale,
  // loss → desaturated white, draw → plain white) so the result reads on red.
  const ftResult = useMemo<'w' | 'd' | 'l' | null>(() => {
    if (m.phase !== 'ft' || !m.score) return null;
    const perspective = arsenalScore(m.score, m.venue, m.arsenalSide)!;
    if (perspective.ours > perspective.theirs) return 'w';
    if (perspective.ours < perspective.theirs) return 'l';
    return 'd';
  }, [m.phase, m.score?.home, m.score?.away, m.venue, m.arsenalSide]);

  const everydayEdition = m.phase === 'idle' || m.phase === 'pre';
  const matchContext = m.matchStatus === 'postponed'
    ? 'This fixture is postponed. COYG will not invent a replacement date or open match calls until a confirmed update arrives.'
    : m.venue === 'A'
    ? 'The next away test, with every change checked against the official calendar.'
    : m.venue === 'H'
      ? 'The next Emirates edition, ready to wake up as matchday approaches.'
      : 'The next Arsenal edition, checked and ready for matchday.';
  const supporterQuestion = m.venue === 'A'
    ? 'What must change away from home?'
    : m.venue === 'H'
      ? 'How should Arsenal take control at home?'
      : 'Where can Arsenal take control?';
  const previousEdition = news.data.today && !isCurrentLondonEdition(news.data.today.date) ? news.data.today : null;
  const lastResultFixturePresent = Boolean(m.lastResult?.matchId && fixtures.data.fixtures.some((fixture) => fixture.id === m.lastResult?.matchId));
  const latestResultCandidate = !lastResultFixturePresent && m.lastResult?.matchId && m.lastResult.postMatch && m.lastResult.playedAt ? m.lastResult : null;
  const latestResultPlayedAt = latestResultCandidate?.playedAt;
  const sourceCheckedAt = selection.lastCheckedAt;
  const latestResult = latestResultCandidate && typeof latestResultPlayedAt === 'string' && Number.isFinite(Date.parse(latestResultPlayedAt))
    ? { ...latestResultCandidate, playedAt: latestResultPlayedAt }
    : null;

  return (
    <div className="screen now-screen">
      <h1 className="visually-hidden">Arsenal right now</h1>
      {/* ── HERO ── */}
      <section className={`hero hero--${staleLive ? 'stale' : presentationPhase}${awaitingResultOnly ? ' hero--awaiting-result' : ''}`} ref={heroRef}>
        <Floodlight phase={presentationPhase} />
        {!everydayEdition && <span className="hero-ghost" aria-hidden="true">{m.opponentShort ?? ''}</span>}
        {everydayEdition ? (
          <div className="hero-inner hero-inner--programme">
            <div className="hero-programme-copy">
              <div className="hero-top">
                <span className="hero-phase">{report ? 'Community update' : awaitingResultOnly ? 'Result awaiting confirmation' : m.matchStatus === 'postponed' ? 'Postponed' : 'Next up'} · {m.comp || PHASE_LABEL[m.phase]}</span>
                <WatchChip tv={resolvedTv} />
              </div>
              <div className="hero-tie">
                <span className="hero-team hero-team--ars">Arsenal</span>
                <span className="hero-vs">{arsenalAway ? '@' : 'v'}</span>
                <span className="hero-team">{m.opponent}</span>
              </div>
              <div className="hero-meta">
                <span>{m.ground ?? venueLabel}</span>
                <span>{fixtureTimeLabel({ kickoffUtc: m.kickoffUtc, kickoffDate: m.kickoffDate, timeTbc: m.timeTbc })}</span>
              </div>
              <div className="hero-actions">
                <button className="programme-primary-action" onClick={() => onOpenMatch(m.matchId)}>{awaitingResultOnly ? 'Open match record' : 'Open match edition'}</button>
                {!report && !report && !awaitingResult && !m.timeTbc && m.matchStatus !== 'postponed' && <button className="programme-secondary-action" onClick={() => addToCalendar(heroFixture)}>Add to calendar</button>}
              </div>
            </div>
            <aside className="hero-programme-meta">
              {!awaitingResultOnly && <StaleBadge stale={match.stale} asOf={match.asOf} />}
              {report ? <CommunityScore report={report} opponentShort={m.opponentShort ?? 'OPP'} venue={arsenalAway ? 'A' : 'H'} /> : !awaitingResultOnly && (m.matchStatus === 'postponed' ? <strong className="programme-kickoff-tbc">Awaiting a new date</strong> : m.timeTbc || !m.kickoffUtc ? <strong className="programme-kickoff-tbc">Kick-off TBC</strong> : <CountdownBlock kickoffUtc={m.kickoffUtc} />)}
              <p>{report ? 'Shared updates are checked about once a minute.' : awaitingResultOnly ? `Source checked ${sourceCheckedAt ? asOfLabel(sourceCheckedAt) : 'Source date unavailable'}. No final result is confirmed yet.` : matchContext}</p>
              <a className="hero-source" href="https://www.arsenal.com/fixtures/men/fixtures" target="_blank" rel="noopener noreferrer">Official fixture →</a>
            </aside>
          </div>
        ) : (
          <div className="hero-inner">
            <div className="hero-top">
              <span className="hero-phase">{staleLive ? 'Last confirmed match update' : PHASE_LABEL[m.phase]}</span>
              <span className="hero-top-right"><WatchChip tv={resolvedTv} /><StaleBadge stale={match.stale} asOf={match.asOf} /></span>
            </div>
            <div className="hero-tie">
              <span className="hero-team hero-team--ars">Arsenal</span>
              <span className="hero-vs">{arsenalAway ? '@' : 'v'}</span>
              <span className="hero-team">{m.opponent}</span>
            </div>
            <div className="hero-meta">
              {m.comp && <span>{m.comp}</span>}
              <span>{fixtureTimeLabel({ kickoffUtc: m.kickoffUtc, timeTbc: m.timeTbc })}</span>
              <span>{venueLabel}</span>
            </div>
            <a className="hero-source" href="https://www.arsenal.com/fixtures/men/fixtures" target="_blank" rel="noopener noreferrer">Check latest fixture details at Arsenal.com ↗</a>
            <div className="hero-cta">

          {liveIsCurrent && (
            <>
              <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
                {liveAnnouncement}
              </div>
              {m.score ? (
                <ScoreLine
                  home={m.score.home}
                  away={m.score.away}
                  opponentShort={m.opponentShort ?? 'OPP'}
                  venue={arsenalAway ? 'A' : 'H'}
                />
              ) : (
                <div className="score-pending">
                  <div className="score-line score-line--pending" aria-label="Score not yet available">
                    <span className="score-team">{arsenalAway ? (m.opponentShort ?? 'OPP') : 'ARS'}</span>
                    <span className="score-nums">
                      <span className="score-pending-cell">–</span>
                      <span className="score-dash">–</span>
                      <span className="score-pending-cell">–</span>
                    </span>
                    <span className="score-team">{arsenalAway ? 'ARS' : (m.opponentShort ?? 'OPP')}</span>
                  </div>
                  <span className="score-pending-sub">
                    {match.online ? 'Live score arriving…' : 'Live score unavailable offline'}
                  </span>
                </div>
              )}
              <div className="hero-minute">
                {m.phase === 'ht' ? 'HT' : m.minute !== null ? `${m.minute}′` : ''}
              </div>
              <div className="hero-actions">
                <button className="ghost-btn" onClick={onOpenTerrace}>
                  Join the North Bank
                </button>
              </div>
            </>
          )}

          {staleLive && (
            <div className="hero-stale-live" role="status">
              <strong>Live view paused</strong>
              <span>This match snapshot is no longer fresh, so COYG is hiding its score, minute and events until a current update arrives.</span>
            </div>
          )}

          {m.phase === 'ft' && m.score && (
            <div className={`ft-score ft-score--${ftResult ?? 'd'}`}>
              <ScoreLine
                home={m.score.home}
                away={m.score.away}
                opponentShort={m.opponentShort ?? 'OPP'}
                venue={arsenalAway ? 'A' : 'H'}
              />
            </div>
          )}
            </div>
          </div>
        )}
      </section>
      <FreeDataAttribution source={fixtures.data.source} />

      {awaitingResult && !awaitingResultOnly && (
        <section className="programme-result-pending" role="status" aria-label="Previous match result status">
          <span>Previous result</span>
          <strong>Result awaiting verification</strong>
          <p>{selection.lastCheckedAt ? `Last checked ${asOfLabel(selection.lastCheckedAt)}. ` : ''}The earlier fixture stays scoreless until a permitted source confirms the result.</p>
        </section>
      )}

      {/* ── Collective H2H read — match-context block, matchday (pre) only.
           Suppressed on idle to avoid competing with "From the archive".
           Self-suppresses when there are no real entries for this opponent. ── */}
      {m.phase === 'pre' && (
        <H2HReadBlock entries={lore.data.entries} opponent={m.opponent} />
      )}

      {/* ── Live: events ── */}
      {liveIsCurrent && (
        <>
          <Rule label="Events" accent="var(--red)" />
          <EventsTimeline events={m.events} players={squad.data.players} onOpenPlayer={onOpenPlayer} />
          <Rule label="The terrace" accent="var(--gold)" />
          <NorthBankLive matchId={m.matchId} onOpen={onOpenTerrace} />
        </>
      )}

      {/* ── Full-time: the Gauge ── */}
      {m.phase === 'ft' && !match.stale && (
        <>
          <Rule label="The gauge" accent="var(--red)" />
          <GaugePanel
            matchId={m.matchId}
            writable={!match.stale}
            matchLabel={
              m.score
                ? `Arsenal ${arsenalScore(m.score, m.venue, m.arsenalSide)!.ours}–${arsenalScore(m.score, m.venue, m.arsenalSide)!.theirs} ${m.opponent}`
                : `Arsenal v ${m.opponent}`
            }
          />
        </>
      )}

      {/* ── Today Lead Dispatch — the morning paper. Renders only off-match
           (idle/pre); during live/HT/FT the match state owns the screen. The
           feed layer merges the bundled dispatch over old-schema/null live
           payloads, so the old "Around the club" list cannot become the
           homepage again because a cached feed is behind. ── */}
      <LivingDesk feed={living} />

      {(m.phase === 'idle' || m.phase === 'pre') && !living.edition && news.data.today && isCurrentLondonEdition(news.data.today.date) && (
        <div className="programme-home-grid">
          <section className="programme-briefing" aria-label="Today’s edition">
            <TodayDispatchBlock
              today={news.data.today}
              stale={news.stale}
              asOf={news.asOf}
              onOpenMatch={onOpenMatch}
              onOpenPlayer={onOpenPlayer}
              onOpenEntry={onOpenEntry}
            />
          </section>
          <aside className="programme-home-rail" aria-label="Today’s supporter question and archive">
            <section className="programme-question">
              <span className="programme-rail-kicker">North Bank question</span>
              <h2>{supporterQuestion}</h2>
              <p>Read the edition first. Add your take when you are ready—consent appears only when you contribute.</p>
              <button type="button" onClick={onOpenTerrace}>Add your take</button>
            </section>
            {(thisDay || (vaultPick && onOpenEntry)) && (
              <section className="programme-archive">
                <span className="programme-rail-kicker">From the archive</span>
                {thisDay && (thisDay.entryId && onOpenEntry ? (
                  <button className="this-day this-day--link" onClick={() => onOpenEntry(thisDay.entryId!)}>
                    <span className="this-day-year">{thisDay.year}</span>
                    <p>{thisDay.text}</p>
                    <span className="last-result-arrow">→</span>
                  </button>
                ) : (
                  <div className="this-day"><span className="this-day-year">{thisDay.year}</span><p>{thisDay.text}</p></div>
                ))}
                {thisDay && <a className="this-day-source" href={thisDay.sourceUrl} target="_blank" rel="noreferrer">Source: {thisDay.sourceLabel} ↗</a>}
                {vaultPick && onOpenEntry && vaultPick.id !== thisDay?.entryId && (
                  <button className="archive-onramp" onClick={() => onOpenEntry(vaultPick.id)}>
                    <span className="archive-onramp-body">
                      <span className="archive-onramp-title">{vaultPick.title}</span>
                      {vaultPick.subtitle && <span className="archive-onramp-sub">{vaultPick.subtitle}</span>}
                    </span>
                    <span className="last-result-arrow">→</span>
                  </button>
                )}
              </section>
            )}
          </aside>
        </div>
      )}

      {(m.phase === 'idle' || m.phase === 'pre') && !living.edition && (!news.data.today || !isCurrentLondonEdition(news.data.today.date)) && (
        <>
          <section className="programme-edition-due" aria-label="Today’s edition status">
            <span>Today’s edition</span>
            <h2>Today’s edition isn’t ready yet.</h2>
            <p>We’ll show the next confirmed edition here.</p>
          </section>
          {latestResult ? (
            <section className="programme-latest-available" aria-label="Latest confirmed result">
              <span>Latest confirmed result</span>
              <h2>{latestResult.postMatch}</h2>
              <p>Confirmed result from {new Intl.DateTimeFormat('en-GB', { dateStyle: 'long', timeZone: 'Europe/London' }).format(new Date(latestResult.playedAt))}.</p>
              <a className="programme-latest-source" href={`/match/${encodeURIComponent(latestResult.matchId)}`}>Open confirmed result →</a>
            </section>
          ) : previousEdition ? (
            <section className="programme-latest-available" aria-label="Previous dated briefing">
              <span>Previous briefing · {new Intl.DateTimeFormat('en-GB', { dateStyle: 'long', timeZone: 'Europe/London' }).format(new Date(`${previousEdition.date}T12:00:00Z`))}</span>
              <h2>{previousEdition.lead.title}</h2>
              <p>{previousEdition.lead.summary}</p>
              {previousEdition.lead.sourceUrl ? (
                <a className="programme-latest-source" href={previousEdition.lead.sourceUrl} target="_blank" rel="noreferrer">Read named source ↗</a>
              ) : (
                <span className="programme-latest-source programme-latest-source--muted">No named source link is available for this saved briefing.</span>
              )}
            </section>
          ) : null}
        </>
      )}

      <section className="programme-wiki-rail" aria-label="COYG wiki">
        <div>
          <span className="programme-rail-kicker">The COYG reference library</span>
          <h2>Find the player, season or Arsenal story behind the day.</h2>
        </div>
        <a href="/wiki">Open the Wiki <span aria-hidden="true">→</span></a>
      </section>

      {/* ── Pre-match: the crowd call. Post-match mood belongs to the Gauge. ── */}
      {(m.phase === 'pre' || m.phase === 'idle') && !report && !awaitingResult && !m.timeTbc && m.matchStatus !== 'postponed' && (
        <section className="programme-prediction">
          <Rule label="Your call" accent="var(--red)" />
          <PredictionStrip matchId={m.matchId} />
        </section>
      )}

      {/* ── Idle: pinned champions memory. The old "Last time out" result strip
           fought the forward-looking Now surface; this keeps one enduring
           emotional anchor without making the homepage feel backwards. ── */}
      {m.phase === 'idle' && <ChampionsPinnedCard lastSeason={club.data.lastSeason} onOpenEntry={onOpenEntry} />}

    </div>
  );
};
